import React from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Close, HistoryRounded } from "@mui/icons-material";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import {
  formatSwayyCouponDiscount,
  formatSwayyCouponUsage,
  formatSwayyCouponDate,
  getSwayyCouponDisplayState,
  isSwayyCouponDateElapsed,
} from "../../../../../models/SwayyCoupon/SwayyCoupon";
import {
  getSwayyCouponChannelLabel,
  getSwayyCouponStatusLabel,
  getSwayyCouponStatusTone,
} from "../../../../../models/SwayyCoupon/SwayyCouponFilters";
import { SwayyStatusTone } from "../../../../../models/SwayyOrder/SwayyOrderFilters";
import { swayyCouponStore } from "../../../stores/SwayyCouponStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";
import SwayyCouponActivityLogsDrawer from "./SwayyCouponActivityLogsDrawer";

const DRAWER_Z_OFFSET = 2000;

const sectionPaperSx = {
  p: { xs: 1.5, sm: 2 },
  borderRadius: swayyRadii.md,
  border: `1px solid ${swayyColors.border}`,
  bgcolor: swayyColors.white,
  boxShadow: "0 8px 28px rgba(0, 81, 84, 0.045)",
};

const StatusChip = ({
  label,
  tone = "pine",
}: {
  label: string;
  tone?: SwayyStatusTone;
}) => {
  const styles: Record<string, { color: string; border: string }> = {
    pine: { color: swayyColors.deepGreen, border: swayyColors.deepGreen },
    success: { color: swayyColors.success, border: swayyColors.success },
    warning: { color: swayyColors.warning, border: swayyColors.warning },
    danger: { color: swayyColors.danger, border: swayyColors.danger },
    muted: { color: swayyColors.textSecondary, border: swayyColors.border },
  };
  const toneStyle = styles[tone];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.1,
        py: 0.3,
        borderRadius: swayyRadii.sm,
        border: `1px solid ${toneStyle.border}`,
        bgcolor: swayyColors.white,
        color: toneStyle.color,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: swayyFont,
        lineHeight: 1.4,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {label.replace(/_/g, " ")}
    </Box>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 0.25, sm: 2 },
      py: 0.85,
      borderBottom: `1px solid ${swayyColors.border}`,
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <CustomTypography
      variant="body3"
      color={swayyColors.textSecondary}
      sx={{
        fontFamily: swayyFont,
        fontSize: 12,
        minWidth: 140,
        fontWeight: 600,
      }}
    >
      {label}
    </CustomTypography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {typeof value === "string" ? (
        <CustomTypography
          variant="body2"
          color={swayyColors.deepGreen}
          sx={{ fontFamily: swayyFont, fontSize: 13, fontWeight: 600 }}
        >
          {value}
        </CustomTypography>
      ) : (
        value
      )}
    </Box>
  </Box>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <CustomTypography
    variant="heading7"
    color={swayyColors.deepGreen}
    sx={{
      fontFamily: swayyFont,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      fontSize: 12,
      mb: 1,
    }}
  >
    {children}
  </CustomTypography>
);

interface SwayyCouponDetailDrawerProps {
  onOpenOrder: (orderId: number) => void;
}

const SwayyCouponDetailDrawer: React.FC<SwayyCouponDetailDrawerProps> = ({
  onOpenOrder,
}) => {
  const store = swayyCouponStore;
  const coupon = store.detail;

  return (
    <>
      <Drawer
        anchor="right"
        open={store.detailOpen}
        onClose={store.closeDetail}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560, md: 720 },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: swayyColors.white,
            borderRadius: 0,
          },
        }}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: store.activityLogsOpen,
          sx: {
            zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
          },
          slotProps: {
            backdrop: {
              sx: {
                zIndex: (muiTheme) =>
                  muiTheme.zIndex.drawer + DRAWER_Z_OFFSET - 1,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 1.5, sm: 2.5 },
            py: 2,
            borderBottom: `1px solid ${swayyColors.border}`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomTypography
                variant="heading6"
                color={swayyColors.deepGreen}
                sx={{
                  fontFamily: swayyFont,
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Coupon Details
              </CustomTypography>
              <Tooltip title="Activity logs">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Activity logs"
                    disabled={store.detailLoading || !coupon}
                    onClick={(event) => {
                      event.stopPropagation();
                      store.openActivityLogs();
                    }}
                    sx={{
                      color: swayyColors.pine,
                      border: `1px solid ${swayyColors.border}`,
                      borderRadius: swayyRadii.sm,
                      backgroundColor: swayyColors.white,
                      "&:hover": {
                        backgroundColor: swayyColors.pineLight,
                      },
                      "&.Mui-disabled": {
                        borderColor: swayyColors.border,
                      },
                    }}
                  >
                    <HistoryRounded fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <CustomTypography
              variant="body2"
              color={swayyColors.textSecondary}
              sx={{ fontFamily: swayyFont, mt: 0.75, display: "block" }}
            >
              {coupon ? coupon.code : "Coupon information"}
            </CustomTypography>
          </Box>
          <IconButton
            onClick={store.closeDetail}
            size="small"
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          {store.detailLoading || !coupon ? (
            <Box
              sx={{
                minHeight: 320,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress sx={{ color: swayyColors.pine }} />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <StatusChip
                  label={getSwayyCouponStatusLabel(
                    getSwayyCouponDisplayState(coupon),
                  )}
                  tone={getSwayyCouponStatusTone(
                    getSwayyCouponDisplayState(coupon),
                  )}
                />
                {coupon.is_expired ? (
                  <StatusChip label="Expired" tone="danger" />
                ) : null}
                <StatusChip
                  label={getSwayyCouponChannelLabel(coupon.channel)}
                  tone="muted"
                />
              </Box>

              <Paper elevation={0} sx={sectionPaperSx}>
                <SectionHeading>Overview</SectionHeading>
                <Divider sx={{ mb: 0.5, borderColor: swayyColors.border }} />
                <DetailRow
                  label="Code"
                  value={
                    <SkuCopyRow
                      sku={coupon.code}
                      emptyFallback="—"
                      copyLabel="Copy coupon code"
                      color={swayyColors.deepGreen}
                      fontWeight={700}
                      typographySx={{ fontFamily: swayyFont, fontSize: 13 }}
                    />
                  }
                />
                <DetailRow
                  label="Description"
                  value={coupon.description || "—"}
                />
                <DetailRow
                  label="Discount"
                  value={formatSwayyCouponDiscount(coupon)}
                />
                <DetailRow
                  label="Min order"
                  value={
                    coupon.min_order_value
                      ? `₹${coupon.min_order_value}`
                      : "No minimum"
                  }
                />
                <DetailRow
                  label="Channel"
                  value={getSwayyCouponChannelLabel(coupon.channel)}
                />
                <DetailRow
                  label="Scope"
                  value={coupon.scope.replace(/_/g, " ")}
                />
              </Paper>

              <Paper elevation={0} sx={sectionPaperSx}>
                <SectionHeading>Usage</SectionHeading>
                <Divider sx={{ mb: 0.5, borderColor: swayyColors.border }} />
                <DetailRow
                  label="Redeemed"
                  value={formatSwayyCouponUsage(coupon)}
                />
                <DetailRow
                  label="Per user"
                  value={String(coupon.usage_per_user)}
                />
                <DetailRow
                  label="First order only"
                  value={coupon.first_order_only ? "Yes" : "No"}
                />
              </Paper>

              <Paper elevation={0} sx={sectionPaperSx}>
                <SectionHeading>Schedule</SectionHeading>
                <Divider sx={{ mb: 0.5, borderColor: swayyColors.border }} />
                <DetailRow
                  label="Starts"
                  value={
                    coupon.starts_at
                      ? formatSwayyCouponDate(coupon.starts_at)
                      : "—"
                  }
                />
                <DetailRow
                  label="Ends"
                  value={
                    coupon.ends_at ? (
                      isSwayyCouponDateElapsed(coupon.ends_at) ? (
                        <StatusChip
                          label={formatSwayyCouponDate(coupon.ends_at)}
                          tone="danger"
                        />
                      ) : (
                        formatSwayyCouponDate(coupon.ends_at)
                      )
                    ) : (
                      "—"
                    )
                  }
                />
                <DetailRow
                  label="Created"
                  value={
                    coupon.created_at ? formatDate(coupon.created_at) : "—"
                  }
                />
                <DetailRow
                  label="Last updated"
                  value={
                    coupon.updated_at ? formatDate(coupon.updated_at) : "—"
                  }
                />
                <DetailRow
                  label="Updated by"
                  value={
                    coupon.updated_by != null
                      ? String(coupon.updated_by)
                      : "—"
                  }
                />
              </Paper>

              <Paper elevation={0} sx={sectionPaperSx}>
                <SectionHeading>
                  Redemptions ({store.redemptions.length})
                </SectionHeading>
                {store.redemptions.length === 0 ? (
                  <CustomTypography
                    variant="body3"
                    color={swayyColors.textSecondary}
                    sx={{ fontFamily: swayyFont }}
                  >
                    No redemptions yet.
                  </CustomTypography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Order</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {store.redemptions.map((redemption) => (
                          <TableRow key={redemption.id}>
                            <TableCell>
                              <AppButton
                                label={`#${redemption.order_id}`}
                                onClick={() =>
                                  onOpenOrder(redemption.order_id)
                                }
                                customStyles={{
                                  minHeight: 28,
                                  px: 0,
                                  color: swayyColors.pine,
                                  fontWeight: 700,
                                  textDecoration: "underline",
                                  backgroundColor: "transparent",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: swayyColors.pineHover,
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell>#{redemption.user_id}</TableCell>
                            <TableCell>₹{redemption.amount}</TableCell>
                            <TableCell>
                              {formatDate(redemption.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </>
          )}
        </Box>
      </Drawer>
      <SwayyCouponActivityLogsDrawer />
    </>
  );
};

export default observer(SwayyCouponDetailDrawer);
